import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { io } from "socket.io-client";
import { envSettings } from "core/EnvSettings";

import { useLocalStorage } from "../api/useLocalStorage";

const socketURL = process.env.NODE_ENV !== 'production' ? envSettings.REACT_APP_API_URL : undefined;
const SocketContext = createContext();

const SocketProvider = ({ children }) => {
	const [socket, setSocket] = useState(null);
	const [error, setError] = useState(null);
	const [isConnected, setIsConnected] = useState(false);
	const [userSetupComplete, setUserSetupComplete] = useState(false);

	const { is_logged_in = null, permissions = null } = useLocalStorage();

	const stablePermissions = useMemo(() => permissions, [JSON.stringify(permissions)]);

	useEffect(() => {
		if (is_logged_in) {
			const socketIo = io(socketURL, {
				withCredentials: true,
			});

			socketIo.on("connect", () => {
				setIsConnected(true);
			});

			socketIo.on("disconnect", () => {
				setIsConnected(false);
			});

			socketIo.on("connect_error", (err) => {
				setError(err);
			});

			socketIo.on("user_setup_complete", (data) => {
				setUserSetupComplete(true);
			});

			setSocket(socketIo);

			return () => {
				socketIo.disconnect();
				socketIo.off("connect");
				socketIo.off("disconnect");
				socketIo.off("connect_error");
				socketIo.off("user_setup_complete");
				setSocket(null);
				setIsConnected(false);
				setUserSetupComplete(false);
			};
		}
	}, [is_logged_in]);

	useEffect(() => {
		if (isConnected && stablePermissions) {
			setUserSetupComplete(false);
			socket?.emit("user_setup", stablePermissions);
		}
	}, [isConnected, stablePermissions]);

	const joinRoom = useCallback(
		(room) => {
			if (socket) {
				socket.emit("join_room", room);
			}
		},
		[socket]
	);

	const contextValue = { socket, error, joinRoom, isConnected, userSetupComplete };

	return <SocketContext.Provider value={contextValue}>{children}</SocketContext.Provider>;
};

const useSocketContext = () => {
	const context = useContext(SocketContext);

	if (!context) {
		throw new Error("useSocket must be used within an SocketProvider");
	}

	return context;
};

export { SocketProvider, useSocketContext };
